/**
 * @module agent/adapters/services/StreamingService
 *
 * Streaming utilities for AgentCore response parsing.
 * Handles Safari compatibility and stream conversion.
 *
 * Responsibilities:
 * - Stream to async iterable conversion (Safari compatibility)
 * - Stream validation
 * - Chunk decoding
 *
 * @example
 * import { streamToAsyncIterable, isStreamable, parseChunk } from './StreamingService';
 *
 * if (isStreamable(response.stream)) {
 *   for await (const chunk of streamToAsyncIterable(response.stream)) {
 *     const decoded = parseChunk(chunk);
 *     console.log(decoded);
 *   }
 * }
 */

// ============================================================================
// STREAM VALIDATION
// ============================================================================

/**
 * Check if a stream can be read.
 * Accepts streams with EITHER Symbol.asyncIterator (Chrome/Firefox) OR getReader() (Safari).
 *
 * @param {any} stream - The stream to validate
 * @returns {boolean} True if stream can be read
 */
export function isStreamable(stream) {
  if (!stream || typeof stream !== 'object') {
    return false;
  }

  // Accept if has Symbol.asyncIterator (Chrome/Firefox)
  if (typeof stream[Symbol.asyncIterator] === 'function') {
    return true;
  }

  // Accept if has getReader() (Safari and all modern browsers)
  if (typeof stream.getReader === 'function') {
    return true;
  }

  return false;
}

// ============================================================================
// STREAM CONVERSION
// ============================================================================

/**
 * Convert a stream to an async iterable.
 * Works on ALL browsers including Safari which lacks Symbol.asyncIterator.
 *
 * @param {ReadableStream|AsyncIterable} stream - The stream to convert
 * @yields {any} Chunks from the stream
 */
export async function* streamToAsyncIterable(stream) {
  // If stream already has Symbol.asyncIterator (Chrome/Firefox), use it directly
  if (typeof stream[Symbol.asyncIterator] === 'function') {
    yield* stream;
    return;
  }

  // Safari fallback: use getReader() API
  const reader = stream.getReader();
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      yield value;
    }
  } finally {
    // IMPORTANT: Use releaseLock() not releaseReader() for Safari compatibility
    reader.releaseLock();
  }
}

// ============================================================================
// CHUNK PARSING
// ============================================================================

/**
 * Parse a chunk into a string.
 * Handles Uint8Array, ArrayBuffer, and string input.
 *
 * @param {Uint8Array|ArrayBuffer|string|null|undefined} chunk - Chunk to parse
 * @returns {string} Decoded string
 */
export function parseChunk(chunk) {
  if (chunk === null || chunk === undefined) {
    return '';
  }

  // Already a string
  if (typeof chunk === 'string') {
    return chunk;
  }

  // ArrayBuffer - wrap in Uint8Array
  if (chunk instanceof ArrayBuffer) {
    return new TextDecoder('utf-8').decode(new Uint8Array(chunk));
  }

  // Uint8Array or similar
  if (chunk.buffer !== undefined || chunk instanceof Uint8Array) {
    return new TextDecoder('utf-8').decode(chunk);
  }

  // Fallback - try to decode anyway
  try {
    return new TextDecoder('utf-8').decode(chunk);
  } catch {
    return String(chunk);
  }
}

// ============================================================================
// TEST UTILITIES
// ============================================================================

/**
 * Create a mock stream with Symbol.asyncIterator (for testing).
 *
 * @param {Array} chunks - Chunks to yield
 * @returns {Object} Mock stream
 */
export function createMockAsyncIterableStream(chunks) {
  return {
    [Symbol.asyncIterator]: async function* () {
      for (const chunk of chunks) {
        yield chunk;
      }
    }
  };
}

/**
 * Create a mock stream with getReader (for Safari testing).
 *
 * @param {Array} chunks - Chunks to yield
 * @returns {Object} Mock stream
 */
export function createMockReaderStream(chunks) {
  return {
    getReader: () => {
      let index = 0;
      return {
        read: async () => {
          if (index >= chunks.length) {
            return { done: true };
          }
          return { done: false, value: chunks[index++] };
        },
        releaseLock: () => {}
      };
    }
  };
}
